import React, { useEffect, useState } from "react";
import { Card, Radio, Typography, Button, Row, Col, Divider, Spin, Modal } from "antd";
import { CheckOutlined, PlusOutlined } from "@ant-design/icons";
import "antd/dist/reset.css";
import { Title3 } from "components/Common/Typography/Titles";
import SubscriptionEditPaymentMethod from "./SubscriptionEditPaymentMethod";
import { listCards, setDefaultCard, addCard, deleteCard } from "api/user.api";
import { notificationController } from "components/Common/Notification/Notification";
import asterisk from "../../images/asterisk.svg";
import ApplePayIcon from "../../images/ApplePayIcon.svg";
import MastercardIcon from "../../images/MastercardIcon.svg";
import VisaIcon from "../../images/VisaIcon.svg";

const { Text, Title } = Typography;


const SubscriptionPaymentMethods: React.FC = () => {
    const [selectedMethod, setSelectedMethod] = useState("visa");
    const [isAdd, setIsAdd] = useState(false);
    const [paymentMethodsData, setPaymentMethodsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cardLoading, setCardLoading] = useState(false);
    const [editingMethod, setEditingMethod] = useState<any | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

    // Define allowed brands
    type CardBrand = "visa" | "mastercard" | "applepay";

    const brandLogos: any = {
        visa: VisaIcon,
        mastercard: MastercardIcon,
        applepay: ApplePayIcon
    };

    const fetchPaymentMethods = async () => {
        try {
            setLoading(true);
            const response = await listCards();
            const paymentMethods = response.data.map((method: any) => ({
                id: method.id,
                name: method.card.display_brand.charAt(0).toUpperCase() + method.card.display_brand.slice(1),
                ending: method.card.last4,
                expiry: `${method.card.exp_month.toString().padStart(2, '0')}/${method.card.exp_year}`,
                logo: brandLogos[method.card.brand] || "", // Provide default fallback
                is_default: method.is_default,
                card: method.card
            }));
            console.log("Payment methods", paymentMethods);
            setPaymentMethodsData(paymentMethods);
        } catch (error) {
            console.error("Error fetching payment methods", error);
        } finally {
            setLoading(false);
        }
    };

    const addCardDetails = async () => {
        const formData = new FormData();
        formData.append("return_url", "https://alpha.sinecure.com/upgrade-plan?templateKey=3");
        try {
            const response = await addCard(formData);
            window.open(response?.session_url, "_blank");
        } catch (error) {
            notificationController.error({
                message: `${error}`
            });
        }
    };

    const showDeleteConfirm = (id: string) => {
        setSelectedMethodId(id);
        setIsModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedMethodId !== null) {
            await deleteCardDetails(selectedMethodId);
        }
        setIsModalVisible(false);
        setSelectedMethodId(null);
    };

    const deleteCardDetails = async (method_id: string) => {
        const formData = new FormData();
        formData.append("payment_method_id", method_id);
        try {
            const response = await deleteCard(formData);
            console.log("Delete Response: ", response);
            notificationController.success({
                message: response?.message
            });
            fetchPaymentMethods();
        } catch (error) {
            notificationController.error({
                message: `${error}`
            });
        }
    };

    useEffect(() => {
        fetchPaymentMethods();
    }, []);

    useEffect(() => {
        console.log("paymentMethodsData===============<>", paymentMethodsData);
    }, [paymentMethodsData]);

    // const paymentMethods = [
    //     { id: "visa", name: "Visa", ending: "1234", expiry: "06/2024", logo: "https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" },
    //     { id: "mastercard", name: "Mastercard", ending: "1234", expiry: "06/2024", logo: "https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" },
    //     { id: "applepay", name: "Apple Pay", ending: "1234", expiry: "06/2024", logo: "https://upload.wikimedia.org/wikipedia/commons/3/30/Apple_Pay_logo.svg" },
    // ];

    if (editingMethod) {
        return <SubscriptionEditPaymentMethod type="edit" cardMethod={editingMethod} />;
    }

    if (isAdd) {
        // return <SubscriptionEditPaymentMethod type="add" />;
        addCardDetails();
    }

    const setAsDefault = async (id: any) => {
        const formData = new FormData();
        setCardLoading(true);
        try {
            console.log("Set as default", id);
            formData.append("payment_method_id", id.toString());
            const response = await setDefaultCard(formData);
            console.log("Set as default response", response);
            setSelectedMethod(id);
            notificationController.success({
                message: response?.message
            });
            fetchPaymentMethods();
        } catch (error) {
            console.error("Error setting as default", error);
            notificationController.error({
                message: `${error}`
            });
        } finally {
            setCardLoading(false);
        }
    };

    return (
        <div className="p-5">
            <div className="px-6 pt-4 pb-12 bg-white">
                <div style={{ flex: 1 }}>
                    <Title level={4} style={{ color: "#101828", fontFamily: "Inter", fontSize: "18px", fontWeight: "600", wordWrap: 'break-word' }}>Payment method</Title>
                    <p style={{ color: "#475467", marginBottom: 0, marginTop: "-10px", fontFamily: "Inter", fontSize: "14px", fontWeight: "400" }}>Update your billing details and address.</p>
                </div>
                <Divider style={{ margin: "20px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginTop: 20 }}>
                    {/* Left side text */}
                    <div style={{ flex: 1 }}>
                        <Text strong style={{ color: "#344054", fontFamily: "Inter", fontSize: "14px", fontWeight: "600" }}>Card details <span style={{ color: "#7F56D9" }}>*</span></Text>
                        <p style={{ color: "#475467", marginBottom: 0, fontFamily: "Inter", fontSize: "14px", fontWeight: "400" }}>Select default payment method.</p>
                    </div>

                    {/* Right side payment method cards */}
                    <div style={{ flex: 2 }}>
                        {
                            <Spin spinning={cardLoading}>
                                {paymentMethodsData.map((method: any) => (
                                    <Card
                                        key={method.id}
                                        style={{
                                            marginBottom: 10,
                                            border: selectedMethod === method.id ? "2px solid #6941C6" : "1px solid #E4E7EC",
                                            borderRadius: 10,
                                            padding: 0,
                                            boxShadow: selectedMethod === method.id ? "0px 0px 10px rgba(146, 84, 222, 0.2)" : "none",
                                            cursor: "pointer",
                                        }}
                                    // onClick={() => setSelectedMethod(method.id)}
                                    >
                                        <Row align="middle" justify="space-between">
                                            {/* Left Section: Logo and Card Info */}
                                            <Col span={16} style={{ display: "flex", alignItems: "center" }}>
                                                <img src={method.logo} alt={method.name} style={{ width: 40, height: 25, marginRight: 12, alignSelf: "start", marginTop: "5px" }} />
                                                <div>
                                                    <Text strong style={{ color: '#344054', fontFamily: 'Inter' }}>{method.name} ending in {method.ending}</Text>
                                                    <br />
                                                    <Text type="secondary" style={{ color: "#475467", fontFamily: "Inter" }}>Expiry {method.expiry} {method.customer}</Text>
                                                    <br />
                                                    <div style={{ marginTop: "7px" }} className="flex">
                                                        {/* <Title3 text="Set as default" color="text-text-secondary" className="font-bold" /> */}
                                                        {
                                                            method.is_default ?
                                                                <>
                                                                    <Text className="font-bold flex mr-2" style={{ color: "#475467", fontFamily: "Inter" }}><img src={asterisk} alt="Asterisk" width={20} className="mr-2" /> Default</Text> &nbsp;
                                                                </>
                                                                :
                                                                <>
                                                                    <Text className="font-bold" style={{ cursor: "pointer", color: "#475467", fontFamily: "Inter" }} onClick={() => setAsDefault(method.id)}>Set as default</Text> &nbsp;
                                                                </>
                                                        }
                                                        {/* <Text style={{ color: "#9254de", cursor: "pointer" , fontWeight: "bold"}}>Edit</Text> */}
                                                        {/* <Text
                                                            style={{ color: "#6941C6", cursor: "pointer", fontWeight: "bold", fontFamily: "Inter" }}
                                                            // onClick={() => setEditingMethod(method)} // Set the method being edited
                                                        >
                                                            Edit
                                                        </Text> */}
                                                    </div>

                                                </div>
                                            </Col>

                                            {/* Right Section: Remove Button & Checkbox */}
                                            <Col span={8} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                                                {/* Checkbox at the top */}
                                                {/* <div
                                                    style={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: "20%",
                                                        backgroundColor: method.is_default ? "#9254de" : "transparent",
                                                        border: method.is_default ? "none" : "1px solid #d9d9d9",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        marginBottom: 8, // Adds spacing between checkbox and remove button
                                                    }}
                                                >
                                                    {method.is_default && <CheckOutlined style={{ color: "white", fontSize: 16 }} />}
                                                </div> */}

                                                {/* Remove button below */}
                                                {
                                                    !method.is_default &&
                                                    <>
                                                        <Button type="link" onClick={() => showDeleteConfirm(method.id)} danger style={{ marginRight: "-28px", color: "#D92D20", fontFamily: "Inter", marginTop: "41px" }}>Remove</Button>
                                                    </>
                                                }
                                            </Col>
                                        </Row>
                                    </Card>
                                ))}
                                {/* Add new payment method button */}
                                <Button onClick={() => setIsAdd(true)} type="text" icon={<PlusOutlined color="#475467" />} style={{ color: "#475467", fontFamily: "Inter", fontWeight: "600", fontSize: "14px" }}>Add new payment method</Button>
                            </Spin>
                        }
                        <Modal
                            title="Confirm Deletion"
                            visible={isModalVisible}
                            onOk={handleConfirmDelete}
                            onCancel={() => setIsModalVisible(false)}
                            okText="Yes"
                            cancelText="Cancel"
                            okButtonProps={{ style: { padding: '8px 16px' } }}
                            cancelButtonProps={{ style: { padding: '8px 16px' } }}
                        >
                            <p>Are you sure you want to remove payment method?</p>
                        </Modal>
                    </div>
                    <div style={{ flex: 1 }}></div>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPaymentMethods;
